import React, { useState, useRef, useCallback, useEffect, memo } from 'react';
// useBlocker requires createBrowserRouter (data router); this app uses
// <BrowserRouter> so we intercept navigation via document-level click handler.
import {
  Typography,
  Box,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Grid,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import PriceScraperV2 from './PriceScraperV2';
import {
  CloudUpload,
  Stop,
  GetApp,
  PlayArrow,
  Description,
  Clear,
  Info,
} from '@mui/icons-material';
import { FaAmazon, FaDollarSign, FaDownload } from 'react-icons/fa';
import { useTheme } from '@mui/material/styles';
import { useProcess } from '../context/ProcessContext';
import FavoriteButton from '../components/FavoriteButton';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

// Memoized so only rows whose data actually changed re-render. Without this,
// every product_update re-renders all ~800 rows, which is the main source of
// UI lag behind the scraper browsers.
const getStatusColorStatic = (status) => {
  switch (status) {
    case 'Found': return 'success';
    case 'Scraping...': return 'warning';
    case 'Not Found': return 'error';
    case 'Error': return 'error';
    default: return 'default';
  }
};

const ProductRow = memo(function ProductRow({ product, isDark }) {
  return (
    <TableRow
      sx={{
        bgcolor: product.status === 'Scraping...'
          ? (isDark ? 'rgba(255, 140, 0, 0.05)' : 'rgba(255, 140, 0, 0.03)')
          : 'transparent',
      }}
    >
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {product.sku}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
          {product.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={product.status}
          size="small"
          color={getStatusColorStatic(product.status)}
          sx={{ fontSize: '0.7rem', height: 22 }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: product.amazonPrice ? '#4CAF50' : 'text.secondary', fontWeight: product.amazonPrice ? 600 : 400 }}>
          {product.amazonPrice || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: product.lowesPrice ? '#4CAF50' : 'text.secondary', fontWeight: product.lowesPrice ? 600 : 400 }}>
          {product.lowesPrice || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: product.walmartPrice ? '#4CAF50' : 'text.secondary', fontWeight: product.walmartPrice ? 600 : 400 }}>
          {product.walmartPrice || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: product.homedepotPrice ? '#4CAF50' : 'text.secondary', fontWeight: product.homedepotPrice ? 600 : 400 }}>
          {product.homedepotPrice || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        {product.amazonUrl ? (
          <Link href={product.amazonUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.75rem' }}>View</Link>
        ) : (<Typography variant="body2" color="text.secondary">-</Typography>)}
      </TableCell>
      <TableCell>
        {product.lowesUrl ? (
          <Link href={product.lowesUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.75rem' }}>View</Link>
        ) : (<Typography variant="body2" color="text.secondary">-</Typography>)}
      </TableCell>
      <TableCell>
        {product.walmartUrl ? (
          <Link href={product.walmartUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.75rem' }}>View</Link>
        ) : (<Typography variant="body2" color="text.secondary">-</Typography>)}
      </TableCell>
      <TableCell>
        {product.homedepotUrl ? (
          <Link href={product.homedepotUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.75rem' }}>View</Link>
        ) : (<Typography variant="body2" color="text.secondary">-</Typography>)}
      </TableCell>
    </TableRow>
  );
});

// Hidden 2026-08-20: every account now scrapes through the EC2 queue, and the
// local bridge needs a desktop helper running on the user's own machine. The
// whole tab-0 flow is left in place — flip this to true to bring it back.
const SHOW_LOCAL_BRIDGE = false;

const PriceScraper = () => {
  const theme = useTheme();
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const fileInputRef = useRef(null);
  // Tab 0 = the existing Quick Scrape flow (runs via the desktop bridge / EB).
  // Tab 1 = the new EC2-backed DB-queued scraper that survives tab close and
  //         shows live progress to anyone who opens the page.
  // Anyone whose saved tab was the bridge must land on the EC2 queue, not on a
  // pane that no longer renders.
  const [activeTab, setActiveTab] = useState(() => {
    if (!SHOW_LOCAL_BRIDGE) return 1;
    const v = localStorage.getItem('priceScraper_activeTab');
    return v === '1' ? 1 : 0;
  });
  const handleTabChange = (_, v) => {
    setActiveTab(v);
    localStorage.setItem('priceScraper_activeTab', String(v));
  };

  // State
  const [selectedFile, setSelectedFile] = useState(null);
  const [products, setProducts] = useState([]);
  const [scraping, setScraping] = useState(false);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('Ready');
  const [isDragging, setIsDragging] = useState(false);
  const [processId, setProcessId] = useState(null);
  const [bridgeConnected, setBridgeConnected] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState('');
  const scrapeStartTimeRef = useRef(null);
  const completedCountRef = useRef(0);

  // Scrape options
  const [scrapeAmazon, setScrapeAmazon] = useState(false);
  const [scrapeWalmart, setScrapeWalmart] = useState(false);
  const [scrapeBBB, setScrapeBBB] = useState(false);
  const [scrapeLowes, setScrapeLowes] = useState(false);
  const [scrapeHomeDepot, setScrapeHomeDepot] = useState(false);

  const logRef = useRef(null);
  const saveTimerRef = useRef(null);  // debounce timer for DB saves
  const wsRef = useRef(null);

  // --- Navigation guards: warn user if scraping is in progress ---
  // We use a ref so event handlers always read the latest value (the
  // beforeunload handler is registered once and must not go stale).
  const scrapingRef = useRef(false);
  useEffect(() => { scrapingRef.current = scraping; }, [scraping]);

  // Guard 1: browser close / tab close / page refresh
  useEffect(() => {
    const handler = (e) => {
      if (!scrapingRef.current) return;
      e.preventDefault();
      e.returnValue = 'Price scraping is still in progress. Are you sure you want to leave?';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []); // register once, check ref

  // Guard 2: in-app navigation (sidebar icons, dropdown menus, breadcrumbs,
  // programmatic navigate() calls — anything that goes through React Router).
  // React Router internally calls history.pushState for every route change,
  // so monkey-patching it catches ALL navigation regardless of whether the
  // trigger was an <a> tag, a <button onClick={navigate(...)}, or a
  // useNavigate() hook call.
  useEffect(() => {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(state, title, url) {
      if (scrapingRef.current) {
        if (!window.confirm('Price scraping is still in progress. Are you sure you want to leave this page?')) {
          return; // block the navigation
        }
      }
      return originalPushState.call(window.history, state, title, url);
    };

    window.history.replaceState = function(state, title, url) {
      if (scrapingRef.current) {
        if (!window.confirm('Price scraping is still in progress. Are you sure you want to leave this page?')) {
          return;
        }
      }
      return originalReplaceState.call(window.history, state, title, url);
    };

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  // Check bridge connection on mount and retry periodically while idle.
  // IMPORTANT: do NOT run this check while a scrape is active — the main
  // scraping WebSocket is already open and serves as its own liveness
  // signal. Opening a throwaway check WebSocket every few seconds used to
  // cause the bridge to treat every close as a "client gone" event and
  // kill the browser mid-scrape.
  useEffect(() => {
    if (scraping) return undefined; // main scraping WS is the liveness signal
    if (activeTab !== 0) return undefined; // V2 tab doesn't use the local bridge

    let retryTimer = null;
    let checkWs = null;

    const checkBridge = () => {
      try {
        checkWs = new WebSocket('ws://localhost:5123');
        checkWs.onopen = () => {
          setBridgeConnected(true);
          checkWs.close();
        };
        checkWs.onerror = () => {
          setBridgeConnected(false);
        };
        checkWs.onclose = () => {
          // If we didn't set connected, it means it failed
        };
      } catch {
        setBridgeConnected(false);
      }
    };

    checkBridge();
    retryTimer = setInterval(checkBridge, 30000);

    return () => {
      clearInterval(retryTimer);
      if (checkWs && checkWs.readyState === WebSocket.OPEN) {
        checkWs.close();
      }
    };
  }, [scraping, activeTab]);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  // Keep only the most recent N log lines so the log panel never has to
  // re-render an ever-growing list. With 3 parallel scraper threads and
  // hundreds of SKUs, thousands of log entries used to make each new
  // setLogs re-render slower than the next one arriving.
  const MAX_LOG_LINES = 400;
  const addLog = useCallback((message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => {
      const next = prev.length >= MAX_LOG_LINES
        ? [...prev.slice(prev.length - MAX_LOG_LINES + 1), `[${timestamp}] ${message}`]
        : [...prev, `[${timestamp}] ${message}`];
      return next;
    });
  }, []);

  // Save results to DB — upserts by (UserNo, SKU). Called immediately on
  // stop/completion and debounced (5s) during active scraping so the DB
  // captures per-site timestamps without hammering the API on every message.
  const saveResultsToDB = useCallback((productsToSave) => {
    if (!productsToSave || productsToSave.length === 0) return;
    const payload = productsToSave
      .filter(p => p.sku && (p.amazonPrice || p.walmartPrice || p.lowesPrice || p.status !== 'Pending'))
      .map(p => ({
        sku: p.sku,
        productName: p.name || '',
        status: p.status || '',
        amazonPrice: p.amazonPrice || '',
        walmartPrice: p.walmartPrice || '',
        lowesPrice: p.lowesPrice || '',
        amazonUrl: p.amazonUrl || '',
        walmartUrl: p.walmartUrl || '',
        lowesUrl: p.lowesUrl || '',
        amazonUpdatedAt: p.amazonPriceAt || null,
        walmartUpdatedAt: p.walmartPriceAt || null,
        lowesUpdatedAt: p.lowesPriceAt || null,
      }));
    if (payload.length === 0) return;
    fetch(API_ENDPOINTS.PRICE_SCRAPER_SAVE_RESULTS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
      },
      body: JSON.stringify({ products: payload }),
    }).catch(() => {}); // fire-and-forget
  }, []);

  // Schedule a debounced save (collapses rapid product_update bursts)
  const scheduleSave = useCallback((productsToSave) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveResultsToDB(productsToSave);
    }, 5000);
  }, [saveResultsToDB]);

  // File handling
  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setError('Please select an Excel file (.xlsx or .xls)');
      return;
    }
    setSelectedFile(file);
    setError(null);
    setProducts([]);
    setLogs([]);
    addLog(`File selected: ${file.name}`);

    // Upload and parse the file
    const formData = new FormData();
    formData.append('file', file);

    apiCall(API_ENDPOINTS.PRICE_SCRAPER_IMPORT, {
      method: 'POST',
      body: formData,
    })
      .then(data => {
        if (data.products && data.products.length > 0) {
          setProducts(data.products.map((p, i) => ({
            id: i,
            sku: p.sku || '',
            name: p.name || p.productName || '',
            status: 'Pending',
            amazonPrice: '',
            walmartPrice: '',
            bbbPrice: '',
            lowesPrice: '',
            amazonUrl: '',
            walmartUrl: '',
            lowesUrl: '',
          })));
          addLog(`Imported ${data.products.length} products`);
        } else {
          setError('No products found in the file');
        }
      })
      .catch(err => {
        setError(`Failed to import file: ${err.message}`);
        addLog(`Error: ${err.message}`);
      });
  }, [addLog]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  }, [handleFileSelect]);

  // Create template
  const handleCreateTemplate = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PRICE_SCRAPER_TEMPLATE, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
      });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'price_scraper_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addLog('Template downloaded');
    } catch (err) {
      setError(`Failed to download template: ${err.message}`);
    }
  }, [addLog]);

  // Start scraping via WebSocket bridge
  const handleStartScraping = useCallback(() => {
    if (products.length === 0 || !bridgeConnected) return;

    setScraping(true);
    setError(null);
    setProgress(0);
    setProgressText('Starting...');
    setEstimatedTime('');
    scrapeStartTimeRef.current = null;
    completedCountRef.current = 0;
    addLog('Starting price scraping...');

    // Reset product statuses
    setProducts(prev => prev.map(p => ({
      ...p,
      status: 'Pending',
      amazonPrice: '',
      walmartPrice: '',
      bbbPrice: '',
      lowesPrice: '',
      homedepotPrice: '',
      url: '',
    })));

    const pid = `price-scraper-${Date.now()}`;
    setProcessId(pid);
    addProcess(pid, 'Price Scraper', 'Scraping prices...', 0);

    try {
      const ws = new WebSocket('ws://localhost:5123');
      wsRef.current = ws;

      ws.onopen = () => {
        // Scraping WS is open — we know the bridge is alive without needing
        // the background health-check to reconfirm.
        setBridgeConnected(true);
        const skus = products.map(p => p.sku || p.name);
        ws.send(JSON.stringify({
          type: 'start',
          skus,
          scrapeAmazon,
          scrapeWalmart,
          scrapeBBB,
          scrapeLowes,
          scrapeHomeDepot,
        }));
        addLog('Connected to bridge, scraping started.');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case 'log':
              addLog(data.message);
              break;
            case 'progress':
              setProgress(data.value * 100);
              setProgressText(data.text);
              updateProcess(pid, { progress: data.value * 100 });
              // ETA calculation based on progress value
              if (data.value > 0 && data.value < 1) {
                if (!scrapeStartTimeRef.current) scrapeStartTimeRef.current = Date.now();
                completedCountRef.current += 1;
                const stepsDone = completedCountRef.current;
                if (stepsDone >= 3) {
                  const elapsed = (Date.now() - scrapeStartTimeRef.current) / 1000;
                  const totalStepsEstimate = elapsed / data.value;
                  const etaSeconds = Math.round(totalStepsEstimate - elapsed);
                  if (etaSeconds > 0) {
                    const hours = Math.floor(etaSeconds / 3600);
                    const mins = Math.floor((etaSeconds % 3600) / 60);
                    const secs = etaSeconds % 60;
                    let etaStr = '';
                    if (hours > 0) etaStr += `${hours}h `;
                    if (mins > 0 || hours > 0) etaStr += `${mins}m `;
                    etaStr += `${secs}s remaining`;
                    setEstimatedTime(etaStr);
                  }
                } else {
                  setEstimatedTime('Calculating estimated time...');
                }
              }
              break;
            case 'product_update':
              setProducts(prev => {
                const now = new Date().toISOString();
                const updated = prev.map((p, i) => {
                  if (i !== data.index) return p;
                  return {
                    ...p,
                    status: data.status || p.status,
                    amazonPrice: data.amazonPrice || p.amazonPrice,
                    walmartPrice: data.walmartPrice || p.walmartPrice,
                    bbbPrice: data.bbbPrice || p.bbbPrice,
                    lowesPrice: data.lowesPrice || p.lowesPrice,
                    homedepotPrice: data.homedepotPrice || p.homedepotPrice,
                    name: data.name || p.name,
                    amazonUrl: data.amazonUrl || p.amazonUrl,
                    walmartUrl: data.walmartUrl || p.walmartUrl,
                    lowesUrl: data.lowesUrl || p.lowesUrl,
                    homedepotUrl: data.homedepotUrl || p.homedepotUrl,
                    // Track per-site timestamps for DB
                    amazonPriceAt: (data.amazonPrice && data.amazonPrice !== p.amazonPrice) ? now : p.amazonPriceAt,
                    walmartPriceAt: (data.walmartPrice && data.walmartPrice !== p.walmartPrice) ? now : p.walmartPriceAt,
                    lowesPriceAt: (data.lowesPrice && data.lowesPrice !== p.lowesPrice) ? now : p.lowesPriceAt,
                    homedepotPriceAt: (data.homedepotPrice && data.homedepotPrice !== p.homedepotPrice) ? now : p.homedepotPriceAt,
                  };
                });
                scheduleSave(updated);
                return updated;
              });
              break;
            case 'completed':
              setScraping(false);
              setProgress(100);
              setProgressText('Completed!');
              setEstimatedTime('');
              addLog('Scraping completed!');
              updateProcess(pid, { progress: 100, status: 'Completed' });
              setTimeout(() => removeProcess(pid), 3000);
              // Flush final results to DB immediately
              setProducts(prev => { saveResultsToDB(prev); return prev; });
              break;
            case 'error':
              setError(data.message);
              setScraping(false);
              addLog(`Error: ${data.message}`);
              removeProcess(pid);
              break;
            default:
              break;
          }
        } catch (parseErr) {
          addLog(`Failed to parse message: ${parseErr.message}`);
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection error. Is the bridge running?');
        setScraping(false);
        removeProcess(pid);
      };

      ws.onclose = () => {
        if (wsRef.current === ws) {
          wsRef.current = null;
        }
      };
    } catch (err) {
      setError(`Failed to connect to bridge: ${err.message}`);
      addLog(`Error: ${err.message}`);
      setScraping(false);
      removeProcess(pid);
    }
  }, [products, bridgeConnected, scrapeAmazon, scrapeWalmart, scrapeBBB, scrapeLowes, scrapeHomeDepot, addProcess, updateProcess, removeProcess, addLog]);

  // Stop scraping via WebSocket
  const handleStopScraping = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'stop' }));
      addLog('Stopping scraper...');
    }
    setScraping(false);
    setProgressText('Stopped');
    setEstimatedTime('');
    // Flush whatever we have to DB
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setProducts(prev => { saveResultsToDB(prev); return prev; });
  }, [addLog, saveResultsToDB]);

  // Export results
  const handleExportResults = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PRICE_SCRAPER_EXPORT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
        },
        body: JSON.stringify({ products }),
      });
      if (!response.ok) throw new Error(`Export failed: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `price_scraper_results_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addLog('Results exported');
    } catch (err) {
      setError(`Failed to export: ${err.message}`);
    }
  }, [products, addLog]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Found': return 'success';
      case 'Scraping...': return 'warning';
      case 'Not Found': return 'error';
      case 'Error': return 'error';
      default: return 'default';
    }
  };

  // Enable Export as soon as ANY product has a price — not just when the
  // final "Found" status is set. This lets users export partial results
  // after Stop, mid-scrape errors, or browser disconnects.
  const hasResults = products.some(p =>
    p.status === 'Found' || p.amazonPrice || p.walmartPrice || p.lowesPrice || p.bbbPrice
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1400, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Price Scraper" category="others" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: { xs: 1, md: 2 },
            mb: { xs: 2, md: 4 },
            fontSize: { xs: '1.25rem', md: '1.5rem' },
          }}
        >
          PRICE SCRAPER
        </Typography>

        {SHOW_LOCAL_BRIDGE && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Quick Scrape (local bridge)" />
          <Tab label="EC2 Job Queue (shared, survives browser close)" />
        </Tabs>
        )}

        {activeTab === 1 && <PriceScraperV2 />}

        {activeTab === 0 && (<>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Bridge Setup Guide - shown when not connected */}
        {!bridgeConnected && (
          <Alert
            severity="warning"
            icon={<Info />}
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={async () => {
                  try {
                    const response = await fetch(API_ENDPOINTS.PRICE_SCRAPER_DOWNLOAD_BRIDGE, {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
                    });
                    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'PriceScraperBridge.zip');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    setError(`Failed to download: ${err.message}`);
                  }
                }}
                sx={{ fontWeight: 600, textDecoration: 'underline' }}
              >
                Download Bridge
              </Button>
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Price Scraper Bridge is not connected
            </Typography>
            <Typography variant="body2" component="div" sx={{ lineHeight: 1.8 }}>
              The Price Scraper requires a small bridge app running on your machine to open the browser and scrape prices. Follow these steps:
              <Box component="ol" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                <li>Click <strong>"Download Bridge"</strong> button to download <code>PriceScraperBridge.zip</code></li>
                <li>Extract the ZIP to any folder on your computer</li>
                <li>Open the extracted folder and run <strong>setup.bat</strong> (first time only - installs Python dependencies)</li>
                <li>Run <strong>install_startup.bat</strong> to auto-start the bridge on Windows login (optional, one time only)</li>
                <li>Run <strong>run_bridge.bat</strong> - a terminal window will open saying "Listening on ws://localhost:5123"</li>
                <li>Come back here - you will see <strong>"Bridge Connected"</strong> in green</li>
              </Box>
              <Box sx={{ mt: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                Requirements: Python 3.9+ and Microsoft Edge (pre-installed on Windows)
              </Box>
            </Typography>
          </Alert>
        )}

        {/* Top Control Bar - everything visible without scrolling */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${theme.palette.divider}`, bgcolor: getCardBackground(theme) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Import */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${isDragging ? TOOL_COLORS.OTHER.primary : theme.palette.divider}`,
                borderRadius: 1,
                px: 2, py: 1,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 1,
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 0, sm: 200 },
                bgcolor: isDragging ? TOOL_COLORS.OTHER.light : 'transparent',
                '&:hover': { borderColor: TOOL_COLORS.OTHER.primary, bgcolor: TOOL_COLORS.OTHER.light },
              }}
            >
              <CloudUpload sx={{ color: theme.palette.text.secondary }} />
              <Typography variant="body2" color="text.secondary" noWrap>
                {selectedFile ? selectedFile.name : 'Import Excel File'}
              </Typography>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={(e) => handleFileSelect(e.target.files[0])} />
            </Box>

            <Button variant="text" size="small" onClick={handleCreateTemplate}
              sx={{ color: theme.palette.text.secondary, fontSize: 12, textTransform: 'none', minWidth: 'auto', '&:hover': { color: TOOL_COLORS.OTHER.primary } }}>
              Sample File
            </Button>

            {/* Site Checkboxes - inline */}
            <FormGroup row sx={{ gap: 0 }}>
              <FormControlLabel
                control={<Checkbox checked={scrapeAmazon} onChange={(e) => setScrapeAmazon(e.target.checked)} size="small" sx={{ color: '#FF9900', '&.Mui-checked': { color: '#FF9900' }, py: 0 }} />}
                label={<Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><FaAmazon size={14} color="#FF9900" /> Amazon</Typography>}
                sx={{ mr: 1 }}
              />
              <FormControlLabel
                control={<Checkbox checked={scrapeWalmart} onChange={(e) => setScrapeWalmart(e.target.checked)} size="small" sx={{ color: '#0071CE', '&.Mui-checked': { color: '#0071CE' }, py: 0 }} />}
                label={<Typography variant="body2">Walmart</Typography>}
                sx={{ mr: 1 }}
              />
              {/* BBB hidden - not working currently */}
              <FormControlLabel
                control={<Checkbox checked={scrapeLowes} onChange={(e) => setScrapeLowes(e.target.checked)} size="small" sx={{ color: '#004990', '&.Mui-checked': { color: '#004990' }, py: 0 }} />}
                label={<Typography variant="body2">Lowe's</Typography>}
                sx={{ mr: 1 }}
              />
              <FormControlLabel
                control={<Checkbox checked={scrapeHomeDepot} onChange={(e) => setScrapeHomeDepot(e.target.checked)} size="small" sx={{ color: '#F96302', '&.Mui-checked': { color: '#F96302' }, py: 0 }} />}
                label={<Typography variant="body2">Home Depot</Typography>}
                sx={{ mr: 1 }}
              />
            </FormGroup>

            {/* Bridge Status */}
            <Chip
              label={bridgeConnected ? 'Bridge Connected' : 'Bridge Not Connected'}
              size="small"
              sx={{ bgcolor: bridgeConnected ? '#4CAF50' : '#f44336', color: 'white', fontWeight: 600, fontSize: '0.7rem' }}
            />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>
              <Tooltip title={!bridgeConnected ? 'Run the Price Scraper Bridge first' : ''}>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleStartScraping}
                    disabled={scraping || products.length === 0 || !bridgeConnected}
                    startIcon={scraping ? <CircularProgress size={14} /> : <PlayArrow />}
                    size="small"
                    sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' }, '&.Mui-disabled': { bgcolor: theme.palette.action.disabledBackground } }}
                  >
                    {scraping ? 'Scraping...' : 'Start'}
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" onClick={handleStopScraping} disabled={!scraping} size="small"
                sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' }, '&.Mui-disabled': { bgcolor: theme.palette.action.disabledBackground } }}
              >
                Stop
              </Button>
              <Button variant="outlined" onClick={handleExportResults} disabled={!hasResults} startIcon={<GetApp />} size="small"
                sx={{ borderColor: TOOL_COLORS.OTHER.primary, color: TOOL_COLORS.OTHER.primary, '&:hover': { borderColor: TOOL_COLORS.OTHER.hover, bgcolor: TOOL_COLORS.OTHER.light } }}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={async () => {
                  try {
                    const response = await fetch(API_ENDPOINTS.PRICE_SCRAPER_DOWNLOAD_BRIDGE, {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
                    });
                    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'PriceScraperBridge.zip');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    setError(`Failed to download bridge: ${err.message}`);
                  }
                }}
                startIcon={<FaDownload />}
                sx={{ borderColor: theme.palette.divider, color: theme.palette.text.secondary, '&:hover': { borderColor: TOOL_COLORS.OTHER.primary, color: TOOL_COLORS.OTHER.primary } }}
              >
                Bridge
              </Button>
            </Box>
          </Box>

          {/* Progress Bar with ETA */}
          {(scraping || progress > 0) && (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">{progressText}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {estimatedTime && scraping && (
                    <Typography variant="caption" sx={{ color: TOOL_COLORS.OTHER.primary, fontWeight: 600 }}>
                      {estimatedTime}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">{Math.round(progress)}%</Typography>
                </Box>
              </Box>
              <LinearProgress variant="determinate" value={progress}
                sx={{ height: 6, borderRadius: 3, bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.OTHER.primary, borderRadius: 3 } }}
              />
            </Box>
          )}
        </Paper>

        <Grid container spacing={2}>
          {/* Results Table - Full Width */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: getCardBackground(theme),
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: { xs: 1.5, md: 2 }, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.25rem' }, minWidth: 0, flexWrap: 'wrap' }}>
                  <FaDollarSign color={TOOL_COLORS.OTHER.primary} />
                  Products to Search
                  {products.length > 0 && (
                    <Chip label={`${products.length} items`} size="small" sx={{ ml: 1 }} />
                  )}
                </Typography>
                {products.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={() => { setProducts([]); setSelectedFile(null); }}
                    disabled={scraping}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 80 }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 180 }}>Product Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 100 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 110 }}>Amazon Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 100 }}>Lowe's Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 110 }}>Walmart Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 120 }}>Home Depot Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 60 }}>Amazon</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 60 }}>Lowe's</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 60 }}>Walmart</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: TOOL_COLORS.OTHER.primary, color: 'white', minWidth: 60 }}>Home Depot</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ textAlign: 'center', py: 8 }}>
                          <Description sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Import an Excel file to get started
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      products.map((product) => (
                        <ProductRow
                          key={product.id}
                          product={product}
                          isDark={theme.palette.mode === 'dark'}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Log Section */}
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: getCardBackground(theme),
              }}
            >
              <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Log
                </Typography>
              </Box>
              <Box
                ref={logRef}
                sx={{
                  p: 2,
                  maxHeight: 150,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
                }}
              >
                {logs.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    Waiting for activity...
                  </Typography>
                ) : (
                  logs.map((log, i) => (
                    <Box key={i} sx={{ color: log.includes('Error') ? '#f44336' : log.includes('completed') ? '#4CAF50' : theme.palette.text.secondary, lineHeight: 1.8 }}>
                      {log}
                    </Box>
                  ))
                )}
              </Box>
            </Paper>

          </Grid>
        </Grid>

        </>)}
      </Box>
    </Box>
  );
};

export default PriceScraper;
